import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { usePortalApi } from "./portal-api";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import { daily } from "./provider";

const syncModuleId = "funkrufgruppen";

export type Funkrufgruppe = {
  readonly id: string;
  readonly rufname: string;
  readonly typ?: string;
  readonly notizen?: string;
};

function useFunkrufgruppenSyncModule(): SyncModule<
  ReadonlyArray<Funkrufgruppe>
> {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `tasks:${syncModuleId}`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      // Radio call groups are a new-Datenportal-only resource — the legacy
      // portal has no equivalent endpoint, so we simply don't fetch anything for it.
      fetchData: useNewApi && baseUrl
        ? () => fetchFunkrufgruppenNew(portalKey, baseUrl)
        : async () => [],
    }),
    [portalKey, useNewApi, baseUrl],
  );
}

async function fetchFunkrufgruppenNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<Funkrufgruppe>> {
  const response = await fetch(
    `${baseUrl}/api/device/sync/radio-call-groups`,
    {
      headers: { Authorization: `Bearer ${portalKey}` },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.radioCallGroups ?? []).map(parseFunkrufgruppeNew);
}

function parseFunkrufgruppeNew(entry: any): Funkrufgruppe {
  return {
    id: entry.id,
    rufname: entry.callSign,
    typ: entry.type || undefined,
    notizen: entry.notes || undefined,
  };
}

export function useFunkrufgruppenSync() {
  const module = useFunkrufgruppenSyncModule();
  useSyncModuleRegistration(module);
}

export function useFunkrufgruppen(): ReadonlyArray<Funkrufgruppe> {
  return useSyncData(syncModuleId) ?? [];
}
